import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';

const roadmapData = [
  {
    quarter: "Q4 2023",
    status: "completed",
    titleKey: "about.roadmapQ42023Title",
    itemKeys: [
      "about.roadmapQ42023Item1",
      "about.roadmapQ42023Item2",
      "about.roadmapQ42023Item3"
    ]
  },
  {
    quarter: "H1 2024",
    status: "completed", 
    titleKey: "about.roadmapH12024Title",
    itemKeys: [
      "about.roadmapH12024Item1",
      "about.roadmapH12024Item2",
      "about.roadmapH12024Item3",
      "about.roadmapH12024Item4",
      "about.roadmapH12024Item5"
    ]
  },
  {
    quarter: "H2 2024",
    status: "completed",
    titleKey: "about.roadmapH22024Title",
    itemKeys: [
      "about.roadmapH22024Item1",
      "about.roadmapH22024Item2",
      "about.roadmapH22024Item3",
      "about.roadmapH22024Item4"
    ]
  },
  {
    quarter: "H1 2025",
    status: "completed",
    titleKey: "about.roadmapH12025Title",
    itemKeys: [
      "about.roadmapH12025Item1",
      "about.roadmapH12025Item2",
      "about.roadmapH12025Item3",
      "about.roadmapH12025Item4",
      "about.roadmapH12025Item5"
    ]
  },
  {
    quarter: "H2 2025", 
    status: "in-progress",
    titleKey: "about.roadmapH22025Title",
    itemKeys: [
      "about.roadmapH22025Item1",
      "about.roadmapH22025Item2",
      "about.roadmapH22025Item3",
      "about.roadmapH22025Item4"
    ]
  },
  {
    quarter: "Q1 2026",
    status: "upcoming", 
    titleKey: "about.roadmapQ12026Title",
    itemKeys: [
      "about.roadmapQ12026Item1",
      "about.roadmapQ12026Item2",
      "about.roadmapQ12026Item3",
      "about.roadmapQ12026Item4",
      "about.roadmapQ12026Item5"
    ]
  }
];

const RoadmapCard = ({ item, index, t, getStatusLabel }) => (
  <motion.div 
    className={`roadmap-item-about roadmap-item--${item.status}`}
    initial={{ opacity: 0, scale: 0.8, y: 10 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ 
      duration: 0.35, 
      delay: index * 0.15,
      type: "spring",
      stiffness: 100
    }}
  >
    <div className="roadmap-content-about">
      <div className="roadmap-header-about">
        <span className="quarter-text">{item.quarter}</span>
        <span className={`quarter-status ${item.status}`}>
          {getStatusLabel(item.status)}
        </span>
      </div>
      
      <h4 className="roadmap-title-item">{t(item.titleKey)}</h4>

      <ul className="roadmap-items-about">
        {item.itemKeys.map((itemKey, idx) => (
          <li key={idx} className="roadmap-subitem-about">
            {t(itemKey)}
          </li>
        ))}
      </ul>
    </div>
  </motion.div>
);

const RoadmapCarousel = () => {
  const { t } = useLanguage();
  const [currentGroup, setCurrentGroup] = useState(0);

  const goToNextGroup = () => setCurrentGroup(currentGroup === 0 ? 1 : 0);
  const goToPrevGroup = () => setCurrentGroup(currentGroup === 0 ? 1 : 0);

  const currentCards = roadmapData.slice(currentGroup * 3, (currentGroup + 1) * 3);

  const getStatusLabel = (status) => {
    switch(status) {
      case 'completed': return t('about.done');
      case 'in-progress': return t('about.working');
      default: return t('about.upcoming');
    }
  };

  return (
    <div className="roadmap-carousel">
      <button 
        className="roadmap-nav-btn roadmap-nav-btn--left"
        onClick={goToPrevGroup}
        aria-label="Previous roadmap items"
      >
        <ChevronLeft size={20} />
      </button>

      <div className="roadmap-timeline-about">
        {currentCards.map((item, index) => (
          <RoadmapCard 
            key={`${currentGroup}-${item.quarter}`}
            item={item}
            index={index}
            t={t}
            getStatusLabel={getStatusLabel}
          />
        ))}
      </div>

      <button 
        className="roadmap-nav-btn roadmap-nav-btn--right"
        onClick={goToNextGroup}
        aria-label="Next roadmap items"
      >
        <ChevronRight size={20} />
      </button>

      <div className="roadmap-indicators">
        <button 
          className={`roadmap-indicator ${currentGroup === 0 ? 'active' : ''}`}
          onClick={() => setCurrentGroup(0)}
          aria-label="Show 2023-2024 roadmap"
        >
          <span className="indicator-year">2023 · 2024</span>
        </button>
        <button 
          className={`roadmap-indicator ${currentGroup === 1 ? 'active' : ''}`}
          onClick={() => setCurrentGroup(1)}
          aria-label="Show 2025-2026 roadmap"
        >
          <span className="indicator-year">2025 · 2026</span>
        </button>
      </div>
    </div>
  );
};

export default RoadmapCarousel;
